import PropTypes from 'prop-types';
// @mui
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
// theme
import { bgBlur } from 'src/theme/css';
// hooks
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
//
import { Link } from 'react-router-dom'; 
import { paths } from 'src/routes/paths';
//
import { useLocation } from 'react-router-dom';
import { HEADER, NAV } from '../config-layout';
import {
  Searchbar,
  AccountPopover,
  SettingsButton,
  LanguagePopover,
  ContactsPopover,
  NotificationsPopover,
} from '../_common';
import Iconify from 'src/components/iconify';
import { getActiveClient } from 'src/_core/config/clientSelector';

// ----------------------------------------------------------------------

export default function Header({ onOpenNav }) {
  const theme = useTheme();

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const siteName = localStorage.getItem('siteName');
  const siteCode = localStorage.getItem('siteCode');

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const basePath = url.pathname;

  function MyButton() {
    const location = useLocation();
    const activeClient = getActiveClient();
  
    const isOnHomePage = location.pathname === paths.dashboard.work_request.home;
    const isOnByDepartmentPage = location.pathname === paths.dashboard.work_request.byDepartment;
    const isOnByLocationPage = location.pathname === paths.dashboard.work_request.byLocation;
    
    // For SMCD_CUSTOMIZATION: Show all three navigation buttons on all pages
    if (activeClient === 'SMCD_CUSTOMIZATION') {
      return (
        <>
          <Link to={paths.dashboard.work_request.byDepartment} style={{ textDecoration: 'none', marginRight: '8px' }}>
            <button 
              className={`button nav-icon-button ${isOnByDepartmentPage ? 'active' : 'inactive'}`}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
              }}
              disabled={isOnByDepartmentPage}
            >
              <Iconify icon="mdi:office-building-outline" width={20} />
              <span className="button__text">Request By Department</span>
            </button>
          </Link>
          <Link to={paths.dashboard.work_request.byLocation} style={{ textDecoration: 'none', marginRight: '8px' }}>
            <button 
              className={`button nav-icon-button ${isOnByLocationPage ? 'active' : 'inactive'}`}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
              }}
              disabled={isOnByLocationPage}
            >
              <Iconify icon="mdi:map-marker-outline" width={20} />
              <span className="button__text">Request By Location</span>
            </button>
          </Link>
          <Link to={paths.dashboard.work_request.home} style={{ textDecoration: 'none' }}>
            <button 
              className={`button nav-icon-button ${isOnHomePage ? 'active' : 'inactive'}`}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              disabled={isOnHomePage}
            >
              <Iconify icon="line-md:home" width={20} />
              <span className="button__text">Home</span>
            </button>
          </Link>
        </>
      );
    }
    
    // For WEBWORK_STANDARD or default: Show simple toggle between New Request and Home
    const buttonLabel = isOnHomePage ? 'New Request' : 'Home';
    const buttonLink = isOnHomePage 
      ? paths.dashboard.work_request.new 
      : paths.dashboard.work_request.home;
  
    const buttonIcon = isOnHomePage 
      ? 'line-md:plus-circle' 
      : 'line-md:home';
  
    return (
      <Link to={buttonLink} style={{ textDecoration: 'none' }}>
        <button 
          className="button" 
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Iconify icon={buttonIcon} width={15} />
          <span className="button__text">{buttonLabel}</span>
        </button>
      </Link>
    );
  }

  const renderContent = (
    <>
      {/* LEFT SECTION - Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <div className="toglebtn">
          <Link to={paths.dashboard.work_request.home} style={{ display: 'inline-block' }}>
            <img
              src={`${basePath}/favicon/Logo Evantage.png`}
              alt="mobileLogo"
              style={{ cursor: 'pointer' }}
            />
          </Link>
        </div>
       
        {/* Company Name on smaller screens */}
        <Box className='HospitalName' sx={{ ml: 2 }}>
      <h4 className='hospital-text'>{siteName}</h4>
      <h4 className='site-text'>{siteCode}</h4>
        </Box>
      </Box>

      {/* SPACER - Push right section to the end */}
      <Box sx={{ flexGrow: 1 }} />

      {/* RIGHT SECTION - Actions */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        {/* Work Request Button */}
        <MyButton />
        
        {/* <SettingsButton /> */}
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          // REMOVED HEADER WIDTH CONSTRAINT FOR FULL WIDTH
          // width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          width: '100%', // Use full width
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            // width: `calc(100% - ${NAV.W_MINI + 1}px)`,
            width: '100%', // Use full width in mini mode too
          }),
        }),
      }}
    > 
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
       
        {renderContent}
      
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  onOpenNav: PropTypes.func,
};
